import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { PageFrame } from '@/components/PageFrame'
import { Button } from '@/components/ui/button'
import { Tab, TabList, TabPanel, Tabs } from '@/components/ui/controls'
import { ErrorState, Skeleton } from '@/components/ui/display'
import { getOrgSettings, saveOrgSettings, settingsError, type OrgSettings } from '@/data/settings'
import { AccessSettings } from './AccessSettings'
import { BrandingSettings } from './BrandingSettings'
import { IntegrationsSettings } from './IntegrationsSettings'
import { RecordingSettings } from './RecordingSettings'
import { SECTION_LABELS, SETTINGS_SECTIONS, type SettingsSection } from './sections'

/**
 * Organisation settings.
 *
 * One draft across all four tabs, one save. The service keeps these in a single
 * config document, so a per-card save button would be four writes pretending to
 * be four independent decisions — and would leave a reader who changed the
 * recording default and then switched tabs with a change they never saved.
 *
 * The bar only appears once something differs from what is stored, and it says
 * how many groups differ rather than just "unsaved changes", because the change
 * being asked about is usually on a tab that is no longer open.
 */
export function SettingsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Non-strict: the route owns the `?tab=` contract, and reading it strictly
  // would mean importing the route the router imports this page from.
  const search = useSearch({ strict: false }) as { tab?: SettingsSection }
  const tab: SettingsSection = SETTINGS_SECTIONS.includes(search.tab as SettingsSection)
    ? (search.tab as SettingsSection)
    : 'recording'

  const settings = useQuery({ queryKey: ['org-settings'], queryFn: getOrgSettings })
  const [draft, setDraft] = useState<OrgSettings | null>(null)

  /**
   * The draft follows the server copy while it is clean, and stops following it
   * the moment it is edited — otherwise connecting the Teams tenant (which
   * refetches) would throw away everything typed on another tab.
   */
  const server = settings.data ?? null
  const dirtyGroups = useMemo(() => changedGroups(server, draft), [server, draft])
  const isDirty = dirtyGroups.length > 0
  // The server rejects the document, not the field, so a save that goes out
  // invalid takes every other pending change down with it.
  const blocker = draft ? settingsError(draft) : null

  useEffect(() => {
    if (!server) return
    setDraft((current) => (current === null || !changedGroups(server, current).length ? server : current))
  }, [server])

  const save = useMutation({
    mutationFn: (next: OrgSettings) => saveOrgSettings(next),
    onSuccess: (saved) => {
      setDraft(saved)
      queryClient.setQueryData(['org-settings'], saved)
      toast.success('Settings saved')
    },
    onError: () => toast.error('Could not save the settings'),
  })

  return (
    <PageFrame
      title="Settings"
      subtitle="nohup"
      footer={
        isDirty ? (
          <div className="flex items-center gap-3">
            {/* The section names are links, because the change being asked
                about is usually on a tab that is no longer open. */}
            {blocker ? (
              <p className="min-w-0 flex-1 truncate text-xs text-[var(--color-danger)]">
                {blocker}
              </p>
            ) : (
              <p className="min-w-0 flex-1 truncate text-xs text-[var(--color-fg-muted)]">
                Unsaved changes in{' '}
                {dirtyGroups.map((section, index) => (
                  <span key={section}>
                    {index > 0 ? ', ' : ''}
                    <button
                      type="button"
                      className="text-[var(--color-fg)] underline underline-offset-2 outline-none hover:text-[var(--color-primary)]"
                      onClick={() => void navigate({ to: '/settings', search: { tab: section } })}
                    >
                      {SECTION_LABELS[section]}
                    </button>
                  </span>
                ))}
              </p>
            )}
            <Button
              size="sm"
              variant="ghost"
              disabled={save.isPending}
              onClick={() => setDraft(server)}
            >
              Discard
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={save.isPending}
              disabled={blocker !== null}
              onClick={() => draft && save.mutate(draft)}
            >
              Save changes
            </Button>
          </div>
        ) : null
      }
    >
      {settings.isPending || !draft || !server ? (
        settings.isError ? (
          <ErrorState
            title="Settings could not be loaded"
            detail={String(settings.error)}
            action={
              <Button size="sm" variant="secondary" onClick={() => void settings.refetch()}>
                Try again
              </Button>
            }
          />
        ) : (
          <div className="space-y-3 pt-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-40 w-full" />
            ))}
          </div>
        )
      ) : (
        <Tabs
          value={tab}
          onValueChange={(next) =>
            void navigate({ to: '/settings', search: { tab: next as SettingsSection } })
          }
        >
          {/* Sticky on the canvas colour, like a section heading — the cards
              scroll under it rather than appearing to slide beneath a bar. */}
          <TabList className="sticky top-0 z-20 -mx-6 bg-[var(--color-canvas)] px-6">
            {SETTINGS_SECTIONS.map((section) => (
              <Tab key={section} value={section}>
                {SECTION_LABELS[section]}
              </Tab>
            ))}
          </TabList>

          <TabPanel value="recording">
            <RecordingSettings draft={draft} onChange={setDraft} />
          </TabPanel>
          <TabPanel value="access">
            <AccessSettings draft={draft} onChange={setDraft} />
          </TabPanel>
          <TabPanel value="branding">
            <BrandingSettings draft={draft} onChange={setDraft} />
          </TabPanel>
          <TabPanel value="integrations">
            <IntegrationsSettings server={server} />
          </TabPanel>
        </Tabs>
      )}
    </PageFrame>
  )
}

/**
 * Which groups differ, by the tab they live on.
 *
 * Structural equality on each subtree rather than one comparison of the whole
 * document: the answer has to name the tab, and a boolean cannot.
 *
 * `integrations.microsoftTeams` is excluded — it is changed by its own buttons,
 * never by the draft, so including it would light the save bar the moment the
 * tenant was connected and offer to "discard" a completed consent grant.
 */
function changedGroups(
  server: OrgSettings | null,
  draft: OrgSettings | null
): SettingsSection[] {
  if (!server || !draft) return []

  const same = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right)
  const groups: Array<[SettingsSection, boolean]> = [
    [
      'recording',
      same(server.recording, draft.recording) &&
        same(server.transcription, draft.transcription) &&
        same(server.bot, draft.bot),
    ],
    ['access', same(server.visibility, draft.visibility) && same(server.deletion, draft.deletion)],
    ['branding', same(server.email, draft.email)],
    [
      'integrations',
      same(
        { ...server.integrations, microsoftTeams: null },
        { ...draft.integrations, microsoftTeams: null }
      ),
    ],
  ]

  return groups.filter(([, unchanged]) => !unchanged).map(([section]) => section)
}
